// Fitting a pole or a surface to a set of points, as the object page (a type's part label, on its
// instances) and the editor (a one-off shape on the selection) both do it: each separate part of the
// points a piece, each piece a task on a worker pool (density.ts runTask), and a summary for the status.
import type { Task, TaskResult } from '../edit/density.ts';
import { type ShapeKind, type PolePiece, type SurfacePiece, MAX_PIECES } from './doc.ts';
import { components, pick, thinPair } from './robust.ts';
import type { PoleFit } from './pole.ts';
import type { SurfaceFit } from './surface.ts';

export interface ShapeParam { key: string; label: string; kind: 'number' | 'select'; def: string | number; min?: number; max?: number; step?: number; options?: string[] }

/** Each kind's settings (the shape's p keeps them, so it can be fitted again the same way). */
export const SHAPE_PARAMS: Record<ShapeKind, ShapeParam[]> = {
  pole: [
    { key: 'radius', label: 'radius m (0: fit)', kind: 'number', def: 0, min: 0, max: 1, step: 0.001 },
    { key: 'taper', label: 'taper', kind: 'select', def: 'no', options: ['no', 'yes'] },
    { key: 'smooth', label: 'smooth', kind: 'number', def: 1, min: 0, max: 100, step: 0.1 },
    { key: 'ctrl', label: 'ctrl pts (0: auto)', kind: 'number', def: 0, min: 0, max: 64 },
  ],
  surface: [
    { key: 'thick', label: 'thickness m (0: fit)', kind: 'number', def: 0, min: 0, max: 1, step: 0.005 },
    { key: 'smooth', label: 'smooth', kind: 'number', def: 1, min: 0, max: 100, step: 0.1 },
    { key: 'cell', label: 'ctrl spacing m (0: auto)', kind: 'number', def: 0, min: 0, max: 2, step: 0.01 },
  ],
};

/** Short names for the settings where room is tight (the full one is the tooltip). */
export const SHAPE_SHORT: Record<string, string> = { radius: 'r m', taper: 'taper', smooth: 'smooth', ctrl: 'ctrl pts', thick: 't m', cell: 'spacing m' };

/** A kind's settings as labelled fields (short names, the full one the tooltip), each writing its value into p as it changes. */
export function shapeParamFields(kind: ShapeKind, p: Record<string, string | number>): HTMLElement[] {
  return SHAPE_PARAMS[kind].map(q => {
    let i: HTMLInputElement | HTMLSelectElement;
    if (q.kind === 'select') {
      i = document.createElement('select');
      for (const o of q.options!) { const e = document.createElement('option'); e.value = e.textContent = o; i.append(e); }
    } else {
      i = document.createElement('input');
      i.type = 'number'; i.style.width = '5.2em';
      if (q.min !== undefined) i.min = String(q.min);
      if (q.max !== undefined) i.max = String(q.max);
      if (q.step) i.step = String(q.step);
    }
    i.value = String(p[q.key] ?? q.def);
    i.onchange = () => { p[q.key] = q.kind === 'number' ? +i.value : i.value; };
    const f = document.createElement('label');
    f.title = q.label;
    f.append(SHAPE_SHORT[q.key] ?? q.label, i);
    return f;
  });
}

const cm = (x: number) => (100 * x).toFixed(1);
const span = (xs: number[]) => { const lo = Math.min(...xs), hi = Math.max(...xs); return hi - lo < 5e-4 ? `${cm(lo)} cm` : `${cm(lo)}–${cm(hi)} cm`; };

/**
 * Fit a pole or a surface to points (packed xyz, with normals), in whatever frame they are given: the
 * pieces come back in it. null pieces: nothing big enough to fit. note: what came of it, e.g.
 * "2 piece(s), r 1.2 cm, 0.9 m long, rms 0.3 cm, 97% of 4,000 points fit".
 */
export async function fitShape(kind: ShapeKind, xyz: Float32Array, nrm: Float32Array, p: Record<string, string | number>,
  exec: (t: Task, transfer?: boolean) => Promise<TaskResult>, progress: (text: string, frac: number) => void): Promise<{ pieces: (PolePiece | SurfacePiece)[] | null; note: string }> {
  const n = xyz.length / 3, comp = components(xyz, 0.04, Math.max(30, Math.round(0.02 * n))), parts = Math.min(comp.n, MAX_PIECES);
  if (!parts) return { pieces: null, note: 'no part big enough to fit' };
  const opts = kind === 'pole'
    ? { nc: +p.ctrl || 0, s: +p.smooth, r: +p.radius || 0, taper: p.taper === 'yes' }
    : { cell: +p.cell || 0, s: +p.smooth, t: +p.thick || 0 };
  let done = 0;
  progress(`fitting ${parts} part(s)`, 0);
  const fits = await Promise.all(Array.from({ length: parts }, async (_, j) => {
    const [px, pn] = thinPair(pick(xyz, comp.part, j), pick(nrm, comp.part, j), kind === 'pole' ? 20000 : 30000);
    const r = await exec({ kind: 'shape', shape: kind, xyz: px, nrm: pn, opts }, true) as PoleFit | SurfaceFit;
    progress(`fitting ${parts} part(s)`, ++done / parts);
    return r;
  }));
  const pieces = fits.map((f): PolePiece | SurfacePiece => 'c' in f ? { c: f.c, r: f.r, rms: f.rms, n: f.n }
    : { o: f.o, u: f.u, v: f.v, ab: f.ab, nu: f.nu, nv: f.nv, h: f.h, t: f.t, mask: f.mask, rms: f.rms, n: f.n });
  const used = fits.reduce((a, f) => a + f.n, 0), rms = Math.sqrt(fits.reduce((a, f) => a + f.rms * f.rms * f.n, 0) / Math.max(1, used));
  const size = kind === 'pole'
    ? `r ${span(fits.flatMap(f => (f as PoleFit).r))}, ${span(fits.map(f => (f as PoleFit).len))} long`
    : `t ${span(fits.map(f => (f as SurfaceFit).t))} (${[...new Set(fits.map(f => (f as SurfaceFit).sides))].join(', ')}), ${fits.reduce((a, f) => a + (f as SurfaceFit).area, 0).toFixed(2)} m²`;
  const warn = new Map<string, number>();
  for (const f of fits) for (const w of f.warn) warn.set(w, (warn.get(w) ?? 0) + 1);
  const left = comp.dropped + comp.part.reduce((a, x) => a + (x >= parts ? 1 : 0), 0);
  return {
    pieces,
    note: `${parts} piece(s), ${size}, rms ${cm(rms)} cm, ${Math.round((100 * used) / n)}% of ${n.toLocaleString()} points fit`
      + (left ? `; ${left.toLocaleString()} in small bits left out` : '')
      + [...warn].map(([w, c]) => `; ${w}${parts > 1 ? ` (${c})` : ''}`).join(''),
  };
}
